import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Mail, Lock, ArrowRight } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  const forgot = async () => {
    if (!email) return toast.error("Enter your email first");
    try { await api.post("/auth/forgot-password", { email }); toast.success("If the account exists, a reset link was logged."); }
    catch (e) { toast.error("Could not process request"); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center dc-grid-bg bg-[#090D16] px-4">
      <div className="w-full max-w-md dc-fade-up">
        <div className="flex justify-center mb-8"><Logo size="lg" onClick={() => navigate("/")} /></div>
        <div className="dc-glass border border-slate-800 rounded-2xl p-8">
          <h1 className="text-2xl font-head font-bold mb-1">Sign in</h1>
          <p className="text-slate-400 text-sm mb-6">Continue to your AI workspace</p>

          <button onClick={googleLogin} data-testid="google-login-btn"
            className="w-full mb-4 h-11 rounded-xl bg-white text-slate-900 font-medium flex items-center justify-center gap-2 hover:bg-slate-100 transition-colors">
            <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4" /> Continue with Google
          </button>

          <div className="flex items-center gap-3 my-5"><div className="h-px flex-1 bg-slate-800" /><span className="text-xs text-slate-500">or</span><div className="h-px flex-1 bg-slate-800" /></div>

          <form onSubmit={submit} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-3.5 h-4 w-4 text-slate-500" />
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full h-11 pl-10 pr-3 rounded-xl bg-slate-900/70 border border-slate-700 text-sm focus:border-emerald-500 focus:outline-none" />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-3.5 h-4 w-4 text-slate-500" />
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full h-11 pl-10 pr-3 rounded-xl bg-slate-900/70 border border-slate-700 text-sm focus:border-emerald-500 focus:outline-none" />
            </div>
            <button type="button" onClick={forgot} className="text-xs text-emerald-400 hover:underline" data-testid="forgot-password-btn">Forgot password?</button>
            <button disabled={busy} data-testid="login-submit"
              className="w-full h-11 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50">
              {busy ? "Signing in..." : <>Sign in <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>
          <p className="text-sm text-slate-400 mt-6 text-center">
            <span className="text-slate-500">Invite-only. Ask the administrator for an account.</span>
          </p>
        </div>
      </div>
    </div>
  );
}
